import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import moment from "moment";
import { GetMasterRecordHistory } from "../../../../Service/_index";
import { useMasterHistoryAccess } from "../../../../UiComponents/MasterHistory/useMasterHistoryAccess";
import MasterHistoryDialog from "../../../../UiComponents/MasterHistory/MasterHistoryDialog";
import MasterAsOfDialog from "../../../../UiComponents/MasterHistory/MasterAsOfDialog";
import { outlinedButtonSx } from "../../../../UiComponents/MasterHistory/HistoryParts";
import {
  formatDateTime,
  formatDay,
  humanizeField,
} from "../../../../UiComponents/MasterHistory/historyFormat";

const HEX24 = /^[0-9a-fA-F]{24}$/;
const MAX_CHIPS = 4;

/**
 * Tells the viewer that the master record a policy was built from has been
 * edited since the policy was created. The policy keeps its own saved values;
 * this only points at the difference. Renders nothing when history is off, the
 * user may not see it, or nothing changed.
 */
const MasterChangedNotice = ({ entityType, entityId, since }) => {
  const { allowed, enabled, entities } = useMasterHistoryAccess();
  const [found, setFound] = useState({ changes: [], recordLabel: "" });
  const [asOfOpen, setAsOfOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const sinceMoment = moment(since);
  const canCheck =
    allowed && enabled && HEX24.test(String(entityId || "")) && Boolean(since) && sinceMoment.isValid();
  const sinceIso = canCheck ? sinceMoment.toISOString() : "";

  useEffect(() => {
    if (!canCheck) return undefined;
    let cancelled = false;
    GetMasterRecordHistory(entityType, entityId).then((res) => {
      if (cancelled || res.error || res.enabled === false) return;
      const changes = (res.data || []).filter(
        (entry) => entry.action !== "baseline" && moment(entry.changedAt).isAfter(sinceIso)
      );
      setFound({ changes, recordLabel: res.recordLabel || "" });
    });
    return () => {
      cancelled = true;
    };
  }, [canCheck, entityType, entityId, sinceIso]);

  if (!canCheck || !found.changes.length) return null;

  const { changes, recordLabel } = found;
  const latest = changes[0];
  const label = entities.find((item) => item.entityType === entityType)?.label || "Master";
  const fields = [...new Set(changes.flatMap((entry) => (entry.changes || []).map((c) => c.field)))];
  const day = formatDay(sinceIso);

  return (
    <Box
      className="no-print"
      component="section"
      aria-label="Master data changes"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: 1.5,
        mb: 2,
        p: { xs: 1.5, sm: 2 },
        border: "1px solid #FEDF89",
        borderRadius: "8px",
        backgroundColor: "#FFFAEB",
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          backgroundColor: "#FEF0C7",
          color: "#B54708",
        }}
      >
        <HistoryIcon sx={{ fontSize: 20 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: "#101828" }}>
          {label} master has changed since this policy was created{" "}
          <Box component="span" sx={{ color: "#B54708", fontWeight: 500 }}>
            ({changes.length} {changes.length === 1 ? "change" : "changes"})
          </Box>
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: "12.5px", color: "#475467" }}>
          {recordLabel ? `${recordLabel} · ` : ""}Last changed by {latest.changedByName || "an unknown user"} on{" "}
          {formatDateTime(latest.changedAt)}. This policy keeps the values it was saved with.
        </Typography>
        {fields.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {fields.slice(0, MAX_CHIPS).map((field) => (
              <Chip
                key={field}
                label={humanizeField(field)}
                size="small"
                sx={{ height: 20, fontSize: "11px", color: "#B54708", backgroundColor: "#FEF0C7" }}
              />
            ))}
            {fields.length > MAX_CHIPS && (
              <Chip
                label={`+${fields.length - MAX_CHIPS} more`}
                size="small"
                sx={{ height: 20, fontSize: "11px", color: "#475467", backgroundColor: "#F2F4F7" }}
              />
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setAsOfOpen(true)}
          sx={{ ...outlinedButtonSx, backgroundColor: "#ffffff" }}
        >
          View as on {day}
        </Button>
        <Button
          size="small"
          onClick={() => setHistoryOpen(true)}
          sx={{ textTransform: "none", fontSize: "12px", fontWeight: 500, color: "#1E3A5F" }}
        >
          View full history
        </Button>
      </Box>

      <MasterAsOfDialog
        open={asOfOpen}
        onClose={() => setAsOfOpen(false)}
        entityType={entityType}
        entityId={String(entityId)}
        asOf={sinceIso}
        onViewHistory={() => {
          setAsOfOpen(false);
          setHistoryOpen(true);
        }}
      />
      <MasterHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        entityType={entityType}
        entityId={String(entityId)}
        recordLabel={recordLabel}
      />
    </Box>
  );
};

export default MasterChangedNotice;
